import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { Container } from '@mui/material';
import { PollardFrame, Environment } from '@finanzcheck/catalyst-frameman';

import { MetaHead } from 'components/MetaHead/MetaHead';
import { HeaderWithOptions } from 'components/Header';
import { Footer } from 'components/Footer';

import handleMessage from 'lib/pollard/pollardFrameMessageHandler';
import {
  getSessionStorage,
  getLocalStorage,
  setLocalStorage,
  setSessionStorage,
} from 'lib/storage/storage';
import { getSessionId } from 'lib/session/sessionHelper';
import { STORAGE_CONSTANTS } from 'lib/constants/storage';
import { fetchPaas } from 'lib/regroute/fetchPaas';
import { urlParamsTranslator } from 'lib/urlParamsConverter/urlParamsConverter';

import { setBackLink } from 'lib/regroute/backLink';

interface IRegrouteData {
  fcid: string | undefined;
  backUrl: string | undefined | null;
  iframeUrl: string | undefined | null;
  iModCurrentTraversalData: string | undefined | null;
}

const initialRegrouteData: IRegrouteData = {
  fcid: undefined,
  backUrl: undefined,
  iframeUrl: undefined,
  iModCurrentTraversalData: undefined,
};

const getImodCurrentTraversalData = () => {
  return JSON.parse(
    getLocalStorage(STORAGE_CONSTANTS.iModCurrentTraversalData) || '{}',
  );
};

const getImodCurrentNavigationData = () => {
  return JSON.parse(
    getSessionStorage(STORAGE_CONSTANTS.iModCurrentNavigation) || '{}',
  );
};

const getResumeHashFromImodCurrentTraversalData = () => {
  return getImodCurrentTraversalData().resumeHash;
};

export default function AntragPage() {
  const [regrouteData, setRegrouteData] = useState(initialRegrouteData);
  const [isPaasCallNeeded, setIsPaasCallNeeded] = useState(false);
  const [isReadyForIframe, setIsReadyForIframe] = useState(false);

  const router = useRouter();
  const {
    advertisementId,
    amount,
    category,
    duration,
    backUrl,
    isAppContext,
    formConfig,
  } = router.query;
  const urlParams = new URLSearchParams(router.query as Record<string, string>);

  const fcid = getSessionId();
  const pollardEnv = process.env
    .REGISTRATION_FRAMEMAN_ENVIRONMENT as Environment;
  // Attaching the messageHandlers
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // updating iMOD current traversal data if there is a formConfig in url
  useEffect(() => {
    const iModCurrentTraversalData = getImodCurrentNavigationData();
    setSessionStorage(
      STORAGE_CONSTANTS.iModCurrentNavigation,
      JSON.stringify({
        ...iModCurrentTraversalData,
        currentConfig: formConfig,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formConfig]);

  // Using the traversal data from storage (normal way)
  useEffect(() => {
    const hashFromTraversalData = getResumeHashFromImodCurrentTraversalData();
    const iModCurrentTraversalData = getImodCurrentTraversalData();

    const isDataInStore = hashFromTraversalData && iModCurrentTraversalData;

    if (isDataInStore) {
      console.info('Resuming with hash:', hashFromTraversalData);
      const fallbackUrl = '/';
      const backUrl =
        getSessionStorage(STORAGE_CONSTANTS.backUrl) || fallbackUrl!;

      const fallbackIframeUrl = process.env.REGISTRATION_FRAME_BASE_URL;
      const originalIframeUrl =
        getLocalStorage(STORAGE_CONSTANTS.iframeUrl) || fallbackIframeUrl;
      const iframeUrl = new URL(originalIframeUrl!);
      for (const [key, value] of urlParams) {
        iframeUrl.searchParams.set(key, value);
      }
      iframeUrl.searchParams.set('resume', hashFromTraversalData);

      for (const [key, value] of urlParams) {
        iframeUrl.searchParams.set(key, value);
      }

      const data = {
        fcid,
        backUrl,
        iframeUrl: iframeUrl.toString(),
        iModCurrentTraversalData,
      };

      setRegrouteData(data);
      setIsReadyForIframe(true);
    } else {
      // We dont have traversal data in storage, trigger useEffect below
      setIsPaasCallNeeded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaasCallNeeded]);

  // Doing a paas call if there is no traversal data in storage (direct link on /antrag)
  useEffect(() => {
    async function doFetchPaas() {
      const preconvertedPaasValues = {
        amount,
        advertisementId: advertisementId || process.env.ADVERTISEMENT_ID,
        category,
        duration,
        clientUuid: process.env.CLIENT_UUID,
        affiliateId: process.env.AFFILIATE_ID,
      };

      const paasPayload = urlParamsTranslator(preconvertedPaasValues);
      const paasResponse = await fetchPaas(paasPayload);

      const isResponseValid = paasResponse && paasResponse.hasError === false;
      if (!isResponseValid) return false;

      const fallbackUrl = '/';
      setBackLink(backUrl?.toString() || fallbackUrl);
      setLocalStorage(STORAGE_CONSTANTS.iframeUrl, paasResponse.result.url);
      setLocalStorage(
        STORAGE_CONSTANTS.iModCurrentTraversalData,
        JSON.stringify(paasResponse.result),
      );
      // We only want to do one paas call, and trigger the useEffect above
      setIsPaasCallNeeded(false);
    }

    if (isPaasCallNeeded) {
      console.info('Direct link, doing fallback Paas call.');
      doFetchPaas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaasCallNeeded]);

  const isAppContextFlag = isAppContext === 'true';
  const frameContainerMarginTop = isAppContextFlag ? '0' : '24px';

  return (
    <>
      <MetaHead follow={false} index={false} />
      <Script id="regRouteFrameLoaded">window.v = new Date().getTime();</Script>
      {!isAppContextFlag && (
        <HeaderWithOptions
          hasLoginLink={false}
          hasNavigationMenu={false}
          hasPhoneBox={false}
        />
      )}

      <Container sx={{ marginTop: frameContainerMarginTop, padding: 0 }}>
        {isReadyForIframe && (
          <PollardFrame
            env={pollardEnv}
            src={regrouteData.iframeUrl!}
            iframeName="loan_application_widget"
            backUrl={regrouteData.backUrl!}
            deactivateHubble
          />
        )}
      </Container>
      {!isAppContextFlag && <Footer />}
    </>
  );
}
